/**
 * Shared deposit display-status resolver.
 *
 * Implements the or-chain for determining what deposit status to render:
 *   1. client_status / client_deposit_status (backend-resolved from
 *      line_items -> top-level client_deposit_status -> date inference)
 *   2. security_deposit_status (supplier side, fallback)
 *   3. "unknown" (only when every source is sentinel/absent)
 *
 * At each step, the sentinel values "unknown", "", null, and undefined
 * are treated as no-information and fall through to the next source.
 *
 * Every surface that renders a resolved deposit status MUST use this
 * helper. No per-surface reimplementation.
 */

/**
 * Returns true if the value carries no real deposit-status information.
 * Sentinels: null, undefined, empty string, whitespace-only, "unknown".
 */
function isDepositStatusSentinel(val) {
  if (val == null) return true;
  const s = String(val).trim().toLowerCase();
  return s === '' || s === 'unknown';
}

/**
 * Resolve the display deposit status from a deposit object.
 *
 * Accepts deposit shapes from both the drilldown
 * ({ client_status, security_deposit_status }) and the recon-trace
 * sibling table ({ client_deposit_status, security_deposit_status }).
 *
 * @param {object|null|undefined} deposit
 * @returns {string} resolved status or "unknown"
 */
export function resolveDepositDisplayStatus(deposit) {
  if (!deposit) return 'unknown';

  // Step 1: client status (backend already resolved line_items ->
  // top-level client_deposit_status -> date inference into this field)
  const clientStatus = deposit.client_status ?? deposit.client_deposit_status;
  if (!isDepositStatusSentinel(clientStatus)) return clientStatus;

  // Step 2: supplier/security deposit status (fallback)
  const securityStatus = deposit.security_deposit_status;
  if (!isDepositStatusSentinel(securityStatus)) return securityStatus;

  // Step 3: every source is sentinel or absent
  return 'unknown';
}
